import React, { useState } from "react";
import {
  FileText,
  Download,
  Eye,
  AlertCircle,
  FileCheck,
  FileX,
  Trash2,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useContractPermissions } from "@/hooks/use-contract-permissions";
import { getAuthHeaders } from "@/lib/auth-utils";
import ContractPreviewModal from "./ContractPreviewModal";

const ContractActions = ({ employee, onContractUpdate }) => {
  const { toast } = useToast();
  const {
    canViewContract,
    canManageContract,
    canDownloadContract,
    canDeleteContract,
  } = useContractPermissions();

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({
    view: false,
    download: false,
    delete: false,
  });
  const [contract, setContract] = useState(null);
  const [contractLoaded, setContractLoaded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Fetch contract info
  const fetchContract = async () => {
    if (contractLoaded || !canViewContract(employee.id)) return;

    try {
      setLoading(true);
      const response = await fetch(
        `/api/v1/employees/${employee.id}/contract`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setContract(data.contract);
        }
      }
    } catch (error) {
      console.error("Error fetching contract:", error);
    } finally {
      setLoading(false);
      setContractLoaded(true);
    }
  };

  // View contract
  const viewContract = async () => {
    if (!contract) return;

    setActionLoading((prev) => ({ ...prev, view: true }));

    // Small delay for visual feedback
    await new Promise((resolve) => setTimeout(resolve, 200));

    setShowPreview(true);
    setActionLoading((prev) => ({ ...prev, view: false }));
  };

  // Download contract
  const downloadContract = async () => {
    if (!contract) return;

    try {
      setActionLoading((prev) => ({ ...prev, download: true }));

      const response = await fetch(
        `/api/v1/employees/${employee.id}/contract/download`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = contract.nom || "contract.pdf";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast({
          title: "Succès",
          description: "Contrat téléchargé avec succès",
        });
      } else {
        throw new Error("Erreur lors du téléchargement");
      }
    } catch (error) {
      console.error("Error downloading contract:", error);
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le contrat",
        variant: "destructive",
      });
    } finally {
      setActionLoading((prev) => ({ ...prev, download: false }));
    }
  };

  // Delete contract (admin/rh only)
  const deleteContract = async () => {
    if (!contract || !canDeleteContract(employee.id)) return;

    try {
      setActionLoading((prev) => ({ ...prev, delete: true }));

      const response = await fetch(
        `/api/v1/employees/${employee.id}/contract`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        }
      );

      if (response.ok) {
        setContract(null);
        setShowDeleteDialog(false);
        toast({
          title: "Succès",
          description: "Contrat supprimé avec succès",
        });
        onContractUpdate?.(); // Refresh parent component
      } else {
        throw new Error("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Error deleting contract:", error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le contrat",
        variant: "destructive",
      });
    } finally {
      setActionLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  React.useEffect(() => {
    fetchContract();
  }, [employee.id]);

  // No access
  if (!canViewContract(employee.id)) {
    return (
      <div className="flex items-center justify-center py-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 border border-gray-200">
                <AlertCircle className="h-4 w-4 text-gray-400" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Accès non autorisé</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  // Loading state
  if (loading && !contractLoaded) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
          <span className="text-sm text-blue-700 font-medium">
            Chargement...
          </span>
        </div>
      </div>
    );
  }

  // No contract
  if (!contract) {
    return (
      <div className="flex items-center justify-center py-2">
        <Badge
          variant="secondary"
          className="text-xs px-3 py-1.5 bg-gray-50 text-gray-600 border border-gray-200 font-medium"
        >
          <FileX className="h-3 w-3 mr-1.5" />
          Aucun contrat
        </Badge>
      </div>
    );
  }

  // Contract available with actions
  return (
    <TooltipProvider>
      <div className="flex items-center gap-3 py-2">
        {/* Contract Status Badge */}
        {/* <Badge
          variant="default"
          className="text-xs px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium shadow-sm"
        >
          <FileCheck className="h-3 w-3 mr-1.5" />
          Contrat disponible
        </Badge> */}

        {/* Action Buttons */}
        <div className="flex items-center gap-1">
          {/* View Contract */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 rounded-lg hover:bg-blue-50 hover:border-blue-200 border border-transparent transition-all duration-200"
                onClick={viewContract}
                disabled={actionLoading.view}
              >
                {actionLoading.view ? (
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                ) : (
                  <Eye className="h-4 w-4 text-blue-600" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-gray-900 text-white">
              <p>Voir le contrat</p>
            </TooltipContent>
          </Tooltip>

          {/* Download Contract */}
          {canDownloadContract(employee.id) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 rounded-lg hover:bg-green-50 hover:border-green-200 border border-transparent transition-all duration-200"
                  onClick={downloadContract}
                  disabled={actionLoading.download}
                >
                  {actionLoading.download ? (
                    <Loader2 className="h-4 w-4 animate-spin text-green-600" />
                  ) : (
                    <Download className="h-4 w-4 text-green-600" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-gray-900 text-white">
                <p>Télécharger</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Delete Contract */}
          {canDeleteContract(employee.id) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 rounded-lg hover:bg-red-50 hover:border-red-200 border border-transparent transition-all duration-200"
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={actionLoading.delete}
                >
                  {actionLoading.delete ? (
                    <Loader2 className="h-4 w-4 animate-spin text-red-600" />
                  ) : (
                    <Trash2 className="h-4 w-4 text-red-600" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-gray-900 text-white">
                <p>Supprimer</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Contract Preview Modal */}
      <ContractPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        employee={employee}
        contract={contract}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              Confirmer la suppression
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600">
              Êtes-vous sûr de vouloir supprimer le contrat de{" "}
              <span className="font-semibold text-gray-900">
                {employee.prenom} {employee.nom}
              </span>{" "}
              ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-gray-300 hover:bg-gray-50"
              disabled={actionLoading.delete}
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={deleteContract}
              disabled={actionLoading.delete}
            >
              {actionLoading.delete ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
};

export default ContractActions;
